import { useSessionStore } from "@auth/sessionStore";
import { captureStatusForRoom, nodesByRoom } from "@capture/captureNodeStatus";
import { useCaptureNodesQuery } from "@capture/captureQueries";
import { CaptureStatusDot } from "@capture/CaptureStatusDot";
import { PatientClientName } from "@components/PatientClientName";
import {
  cn,
  LabelTape,
  PinnedPaper,
  Swipe,
} from "@scribenote/brian-design-system";
import {
  type CardSeverity,
  maxSeverity,
  severityReaderChrome,
} from "@subjects/cardSeverity";
import { formatTime } from "@utils/format-date";
import { usePatientSeverities } from "@views/viewsQueries";
import type { TodayVisit } from "@visits/visitsClient";
import { useOpenRoom } from "@workspace/useOpenRoom";
import { useRoomOccupants } from "@workspace/useRoomOccupants";

import {
  groupRoomOccupants,
  type RoomOccupantGroup,
} from "./groupRoomOccupants";
import type { Room } from "./roomsClient";

/** A flagged patient's chrome (wash tone + icon), or null when calm. */
type OccupantChrome = ReturnType<typeof severityReaderChrome>;

/**
 * One patient currently in the room: the name leads, and stage + scheduled
 * time share one quiet metadata line beneath it (stage in body ink so it
 * reads first). Spans throughout: these rows live inside the tile's button.
 */
const RoomOccupant = ({
  visit,
  chrome,
}: {
  visit: TodayVisit;
  /** The patient's severity wash + icon over their name, or none when calm. */
  chrome: OccupantChrome;
}) => {
  return (
    <span className="block min-w-0">
      <span className="block truncate font-display text-[0.9375rem] font-medium text-ink">
        {chrome ? (
          // Only the patient takes the wash: the household part is annotation
          // ink, and annotation under a wash multiplies to mud. The icon rides
          // inside the wash so severity never rests on hue alone.
          <>
            {/* Swipe hides its icon from AT because normally its own child is
                the status word. Here the child is a patient name, so the
                severity has to be written out or the marked patient reads as
                calm. */}
            <span className="sr-only">{chrome.label}: </span>
            {/* An icon makes Swipe an inline-flex box, and the parent's
                `truncate` cannot ellipsise one of those - a long name would be
                clipped mid-glyph. So the marked name truncates inside the
                swipe: `max-w-full` bounds the box, `min-w-0` lets the word
                shrink, `block` gives text-overflow something to work on. */}
            <Swipe
              tone={chrome.tone}
              icon={<chrome.icon className="text-xs" />}
              className="max-w-full align-bottom"
            >
              <span className="block min-w-0 truncate">
                {visit.patientName}
              </span>
            </Swipe>
            {visit.clientName && (
              <span className="font-normal text-annotation">
                {" "}
                – {visit.clientName}
              </span>
            )}
          </>
        ) : (
          <PatientClientName
            patientName={visit.patientName}
            clientName={visit.clientName}
            clientClassName="font-normal text-annotation"
          />
        )}
      </span>
      {(visit.currentStage || visit.scheduledStart) && (
        <span className="mt-0.5 flex min-w-0 items-baseline gap-1.5 text-xs">
          {visit.currentStage && (
            <span className="truncate font-medium text-ink-body">
              {visit.currentStage.name}
            </span>
          )}
          {visit.currentStage && visit.scheduledStart && (
            <span aria-hidden="true" className="shrink-0 text-annotation">
              ·
            </span>
          )}
          {visit.scheduledStart && (
            <span className="shrink-0 font-mono text-fine-print text-annotation">
              {formatTime(visit.scheduledStart)}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

/**
 * A single client with several pets in the room, collapsed into one row so a
 * litter doesn't stack N near-identical occupant rows and grow the tile without
 * bound (WEB-3216). The client leads (there's no single patient to name), with the
 * pet names truncated beneath. The pet count reads off the subtitle, which is the
 * only place it lives now that the badged avatar is gone. Opening the room still
 * lists every pet individually.
 */
const MultiPetOccupant = ({
  group,
  chrome,
}: {
  group: RoomOccupantGroup;
  /** The worst severity across the group's pets, washed over the client name. */
  chrome: OccupantChrome;
}) => {
  const petNames = group.visits.map((visit) => visit.patientName).join(", ");
  const name = group.clientName ?? "Multi-pet appointment";
  return (
    <span className="block min-w-0">
      <span className="block truncate font-display text-[0.9375rem] font-medium text-ink">
        {chrome ? (
          <>
            <span className="sr-only">{chrome.label}: </span>
            <Swipe
              tone={chrome.tone}
              icon={<chrome.icon className="text-xs" />}
              className="max-w-full align-bottom"
            >
              <span className="block min-w-0 truncate">{name}</span>
            </Swipe>
          </>
        ) : (
          name
        )}
      </span>
      <span className="block truncate text-xs text-annotation">
        Multi-pet appointment · {group.visits.length} pets
      </span>
      <span className="block truncate text-[0.8125rem] text-ink-body">
        {petNames}
      </span>
    </span>
  );
};

/**
 * The tile's own ring inks, NOT the shared severity chrome: the wash ring the
 * task cards wear glares against the cork (owner call, WEB-3366), so the board
 * keeps the quiet status ink and the wash moves onto the occupants' names.
 */
const ROOM_RING: Record<Exclude<CardSeverity, "calm">, string> = {
  watch: "ring-ink-flag",
  urgent: "ring-ink-stopped",
};

/**
 * One room on the clinic corkboard: a paper card pinned to the cork, the room's
 * label-maker tape across the top and its occupants written below. The whole
 * card is one button; hovering lifts the paper, it never rotates (interactive).
 */
export const RoomTile = ({ room }: { room: Room }) => {
  // Opens the patient in the room, not the room (WEB-3288).
  const openRoom = useOpenRoom();
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  // A room may hold several patients (a multi-pet visit) - one row per pet.
  const occupants = useRoomOccupants(room.id);
  // Ring the card in status ink for the most urgent surfaced view across its
  // occupants (WEB-3214 / WEB-3251), so urgency reads from the clinic board
  // before you open the room. Calm rooms are plain paper. The tile keeps the
  // quiet ink ring rather than the shared wash chrome (glare on cork, owner
  // call); the wash goes over the flagged occupants' names instead, so the
  // board says WHO as well as where.
  const severities = usePatientSeverities(occupants.map((o) => o.patientId));
  const severity = maxSeverity(
    occupants.map((o) => severities.get(o.patientId) ?? "calm"),
  );
  const patientChrome = (patientId: string): OccupantChrome =>
    severityReaderChrome(severities.get(patientId) ?? "calm");
  // Per-room capture status from the node roster (WEB-3252). Non-suspending:
  // the dot is a nice-to-have, so a missing/slow roster leaves the tile intact.
  // `data` is undefined until the first fetch resolves - treat as no node.
  const { data: nodes } = useCaptureNodesQuery(sessionKey);
  const captureStatus = captureStatusForRoom(
    nodesByRoom(nodes ?? []).get(room.id) ?? [],
  );
  // Collapse each client's pets into one row so a litter doesn't overflow the
  // tile (WEB-3216). Severity ringing above still spans every pet in the room.
  const entries = groupRoomOccupants(occupants);

  return (
    // data-intercom-target mirrors data-testid, as DSCardWrapper stamped it.
    <PinnedPaper
      as="button"
      type="button"
      data-testid={`room-tile-${room.id}`}
      data-intercom-target={`room-tile-${room.id}`}
      onClick={() => openRoom(room.id)}
      className={cn(
        // flex-col overrides the browser's default button rendering, which
        // vertically centers content once the grid's equal-height stretch
        // (RoomsGrid) makes the button taller than its content - without
        // this, a light tile's tape and occupants float mid-card instead of
        // pinning to the top like a fuller neighbour tile.
        "flex w-full flex-col cursor-pointer border border-rule p-4 text-left",
        "transition duration-200 ease-paper hover:-translate-y-px hover:shadow-lift-lg motion-reduce:transition-none",
        "focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent",
        // Ring, not border: the base border-rule keeps the paper's own edge and
        // the two never fight over border-color in the cascade.
        severity !== "calm" && cn("ring-2", ROOM_RING[severity]),
      )}
    >
      {/* The action as content, not an aria-label: a label on the button would
          replace this whole subtree in the accessible name, so the occupants and
          their severity marks would never be announced. */}
      <span className="sr-only">Open room</span>
      <span className="flex min-h-20 flex-col gap-2">
        <span className="flex min-w-0 items-center gap-2">
          {/* Room names are unbounded free text: the tape truncates rather than
              pushing the capture dot off the card. */}
          <LabelTape className="min-w-0 truncate">{room.name}</LabelTape>
          <CaptureStatusDot status={captureStatus} roomId={room.id} />
        </span>
        {/* The grid only renders occupied rooms (WEB-3221), so there is always
            at least one occupant here. A faint rule between patients does the
            grouping; the wrappers carry it so multi-pet rows get it too. */}
        <span className="flex flex-col divide-y divide-rule/50">
          {entries.map((entry) => (
            <span
              key={
                entry.visits.length === 1
                  ? entry.visits[0].id
                  : `client-${entry.clientId}`
              }
              className="py-2 first:pt-0 last:pb-0"
            >
              {entry.visits.length === 1 ? (
                <RoomOccupant
                  visit={entry.visits[0]}
                  chrome={patientChrome(entry.visits[0].patientId)}
                />
              ) : (
                <MultiPetOccupant
                  group={entry}
                  chrome={severityReaderChrome(
                    maxSeverity(
                      entry.visits.map(
                        (v) => severities.get(v.patientId) ?? "calm",
                      ),
                    ),
                  )}
                />
              )}
            </span>
          ))}
        </span>
      </span>
    </PinnedPaper>
  );
};
